import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { App as CapacitorApp } from '@capacitor/app'
import { getNativePlatform } from '../utils/platform'
import { loadOnboardingDraft } from '../utils/onboardingDraft'

// Custom-scheme deep links into the native shell.
//
// Today the only producer is the Oura OAuth round-trip: after the
// server-side token exchange, app/api/oura.py redirects Safari to
// haiven://oura?status=connected|error, iOS switches back into the app,
// and Capacitor delivers the URL here via appUrlOpen (warm return — the
// usual case, since the app is merely backgrounded during OAuth) or
// getLaunchUrl (cold start, if iOS killed the app in the meantime).
//
// The "haiven" scheme and "oura" host must match Info.plist
// (CFBundleURLTypes) and the backend's _NATIVE_*_REDIRECT constants —
// a mismatch means the redirect silently strands the user in Safari.
//
// Routes to /wearables: that screen owns the Connect button the user
// just tapped and watches its own location.search for this ?oura=
// return trip (not just a fresh mount — the warm-return case above
// lands back on the same already-mounted instance) to clear its
// "Connecting…" state, re-fetch /oura/status, and show the Connected
// pill immediately.

const OURA_DEEPLINK_PREFIX = 'haiven://oura'

// Pure parser, exported for tests: deep-link URL → in-app route (or
// null when the URL isn't ours to handle). `onboardingInProgress`
// switches the destination: mid-onboarding the Connect button lives on
// the wearable step, so the return must resume /onboarding (its ?oura=
// effect advances past that step) rather than open the settings screen.
export function ouraDeepLinkTarget(url, { onboardingInProgress = false } = {}) {
  if (!url || !url.startsWith(OURA_DEEPLINK_PREFIX)) return null
  const status = /[?&]status=([^&]+)/.exec(url)?.[1]
  const query = `oura=${status === 'connected' ? 'connected' : 'error'}`
  return onboardingInProgress ? `/onboarding?${query}` : `/wearables?${query}`
}

export default function DeepLinkHandler() {
  const navigate = useNavigate()

  useEffect(() => {
    const platform = getNativePlatform()
    if (!platform || platform === 'web') return undefined

    const handle = (url) => {
      // The onboarding draft is written immediately before the OAuth
      // hand-off (Onboarding's handleConnectOura) and cleared on
      // completion or fresh entry — its presence is the signal that
      // this OAuth round-trip started mid-onboarding.
      const target = ouraDeepLinkTarget(url, {
        onboardingInProgress: Object.keys(loadOnboardingDraft()).length > 0,
      })
      // Not `replace: true`: appUrlOpen fires asynchronously after iOS
      // foregrounds the app, and can land after the user has already
      // navigated elsewhere (e.g. tapped back off the stale "Connecting…"
      // screen while waiting). A replace would silently overwrite
      // whatever history entry happens to be current at that moment —
      // observed in the wild as a dead-end back button after connecting
      // Oura. A normal push costs one extra back-tap in that race at
      // worst, instead of corrupting the stack.
      if (target) navigate(target)
    }

    let listener
    CapacitorApp.addListener('appUrlOpen', ({ url }) => handle(url))
      .then((l) => { listener = l })
      .catch(() => { /* older shell without the App plugin */ })
    CapacitorApp.getLaunchUrl()
      .then((launch) => handle(launch?.url))
      .catch(() => { /* no launch URL — normal cold start */ })

    return () => { listener?.remove?.() }
  }, [navigate])

  return null
}
